import React, { useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { icons as collectionIcons } from '@/components/CollectionIconsGrid';

// Only siblings can be reordered: dragging never moves a collection out of its folder.
export default function SidebarCollectionOrderEditor({ items, onReorder }) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const draggedId = useRef(null);
  const [dropTarget, setDropTarget] = useState(null);
  const clearDrag = () => { draggedId.current = null; setDropTarget(null); };
  const move = (group, index, offset) => {
    const target = index + offset;
    if (target < 0 || target >= group.length) return;
    const next = [...group];
    [next[index], next[target]] = [next[target], next[index]];
    onReorder(next.map(item => item.id));
  };

  const renderGroup = (group, depth = 0) => group.map((item, index) => {
    const content = <>
      <Icon name={collectionIcons[item.icon]?.icon || 'folder'} size={18} color={colors.text_color_secondary} />
      <Text numberOfLines={1} style={styles.title}>{item.title}</Text>
    </>;
    const desktopWeb = Platform.OS === 'web' && !isXS;
    const edge = event => event.clientY < event.currentTarget.getBoundingClientRect().top + event.currentTarget.getBoundingClientRect().height / 2 ? 'before' : 'after';
    const canDrop = () => draggedId.current !== item.id && group.some(candidate => candidate.id === draggedId.current);
    return <React.Fragment key={item.id}>
      {desktopWeb ? <div
        data-testid={`reorder-collection-${item.id}`}
        draggable
        tabIndex={0}
        role="button"
        aria-label={`Collection: ${item.title}`}
        aria-description="Drag to reorder within this folder, or use the up and down arrow keys."
        onKeyDown={event => {
          if (!['ArrowUp', 'ArrowDown'].includes(event.key)) return;
          event.preventDefault();
          event.stopPropagation();
          move(group, index, event.key === 'ArrowUp' ? -1 : 1);
        }}
        onDragStart={event => {
          draggedId.current = item.id;
          event.dataTransfer.effectAllowed = 'move';
          event.dataTransfer.setData('text/plain', item.id);
        }}
        onDragOver={event => {
          if (!canDrop()) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = 'move';
          const position = edge(event);
          setDropTarget(current => current?.id === item.id && current.edge === position ? current : { id: item.id, edge: position });
        }}
        onDragLeave={event => {
          if (!event.currentTarget.contains(event.relatedTarget)) setDropTarget(null);
        }}
        onDrop={event => {
          event.preventDefault();
          if (canDrop()) {
            const ids = group.map(candidate => candidate.id).filter(id => id !== draggedId.current);
            ids.splice(ids.indexOf(item.id) + (edge(event) === 'after' ? 1 : 0), 0, draggedId.current);
            onReorder(ids);
          }
          clearDrag();
        }}
        onDragEnd={clearDrag}
        style={{ marginLeft: depth * 16, cursor: 'grab', borderRadius: 6,
          borderTop: `2px solid ${dropTarget?.id === item.id && dropTarget.edge === 'before' ? colors.text_color_primary : 'transparent'}`,
          borderBottom: `2px solid ${dropTarget?.id === item.id && dropTarget.edge === 'after' ? colors.text_color_primary : 'transparent'}` }}
      >
        <View style={styles.row}>
          <View style={styles.grip}>
            {[0, 1].map(column => <View key={column} style={styles.gripColumn}>{[0, 1, 2].map(dot => <View key={dot} style={[styles.gripDot, { backgroundColor: colors.text_color_secondary }]} />)}</View>)}
          </View>
          {content}
        </View>
      </div> : <View style={[styles.row, { marginLeft: depth * 16 }]}>
        {content}
        {[-1, 1].map(offset => {
          const disabled = index + offset < 0 || index + offset >= group.length;
          return <Pressable key={offset} accessibilityRole="button"
            accessibilityLabel={`Move ${item.title} ${offset < 0 ? 'up' : 'down'}`}
            disabled={disabled} onPress={() => move(group, index, offset)}
            style={[styles.arrow, { opacity: disabled ? 0.3 : 1 }]}>
            <Icon name={offset < 0 ? 'arrow-up' : 'arrow-down'} size={14} color={colors.text_color_primary} />
          </Pressable>;
        })}
      </View>}
      {renderGroup(item.children || [], depth + 1)}
    </React.Fragment>;
  });

  return <>
    <View style={styles.message}>
      <Text size="s" color="secondary">{Platform.OS === 'web' && !isXS ? 'Drag collections to reorder. You can also use the up and down arrow keys.' : 'Use the arrows to reorder your collections.'}</Text>
      <Text size="xs" color="tertiary">Changes save automatically on this device.</Text>
    </View>
    {renderGroup(items)}
  </>;
}

const styles = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 8, minHeight: 40 },
  title: { flex: 1 },
  arrow: { padding: 10 },
  grip: { width: 14, flexDirection: 'row', gap: 3, justifyContent: 'center' },
  gripColumn: { gap: 3 },
  gripDot: { width: 2.5, height: 2.5, borderRadius: 2 },
  message: { paddingHorizontal: 10, paddingVertical: 6, gap: 4 },
});
